import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type {
  OrdenPagoEnvioEmailPreview,
  OrdenPagoEnvioEmailRequest,
} from '../../services/tesoreriaService';
import { useAuth } from '../../contexts/AuthContext';
import { canManageSistemaUsers, effectiveUserRoles } from '../../utils/sistemaAuthz';
import { isValidEmail, splitEmails, stripHtml } from '../../utils/emailEnvioHelpers';

interface EnviarOrdenPagoEmailDialogProps {
  open: boolean;
  ordenPagoNumero: number | null;
  preview: OrdenPagoEnvioEmailPreview | null;
  loading?: boolean;
  sending?: boolean;
  onClose: () => void;
  onSend: (payload: OrdenPagoEnvioEmailRequest) => void;
  onValidationError?: (message: string) => void;
}

const EnviarOrdenPagoEmailDialog: React.FC<EnviarOrdenPagoEmailDialogProps> = ({
  open,
  ordenPagoNumero,
  preview,
  loading = false,
  sending = false,
  onClose,
  onSend,
  onValidationError,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeConfigurarCorreo = canManageSistemaUsers(effectiveUserRoles(user?.roles));
  const [destinatarios, setDestinatarios] = useState('');
  const [cc, setCc] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !preview) return;
    setDestinatarios(preview.emailsSugeridos.join('; '));
    setCc('');
    setAsunto(preview.asuntoSugerido ?? '');
    setMensaje(stripHtml(preview.mensajeSugerido ?? ''));
    setEmailError(null);
  }, [open, preview]);

  const destList = splitEmails(destinatarios);
  const ccList = splitEmails(cc);
  const invalidDest = destList.filter((e) => !isValidEmail(e));
  const invalidCc = ccList.filter((e) => !isValidEmail(e));

  const handleSubmit = () => {
    if (destList.length === 0) {
      const msg = 'Ingrese al menos un correo electrónico válido en Para.';
      setEmailError(msg);
      onValidationError?.(msg);
      return;
    }
    if (invalidDest.length > 0 || invalidCc.length > 0) {
      const invalid = [...invalidDest, ...invalidCc];
      const msg = `Correo inválido: ${invalid.join(', ')}. Debe incluir @ y dominio (ej. proveedor@empresa.com).`;
      setEmailError(msg);
      onValidationError?.(msg);
      return;
    }
    setEmailError(null);
    onSend({
      destinatarios: destList,
      cc: ccList.length > 0 ? ccList : undefined,
      asunto: asunto.trim() || undefined,
      mensaje: mensaje.trim() || undefined,
    });
  };

  const puedeEnviar = Boolean(
    preview?.smtpConfigurado &&
    destList.length > 0 &&
    invalidDest.length === 0 &&
    invalidCc.length === 0 &&
    asunto.trim()
  );

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Enviar orden de pago por mail{ordenPagoNumero ? ` #${ordenPagoNumero}` : ''}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={sending}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 3, gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Cargando datos del envío…
            </Typography>
          </Box>
        ) : preview ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!preview.smtpConfigurado && (
              <Alert
                severity="warning"
                action={
                  puedeConfigurarCorreo ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        onClose();
                        navigate('/configuraciones/correo-salida');
                      }}
                    >
                      Configurar ahora
                    </Button>
                  ) : undefined
                }
              >
                El envío por correo no está configurado para esta empresa.
                {puedeConfigurarCorreo
                  ? ' Configurá la cuenta compartida desde Administración → Correo para facturas.'
                  : ' Pedile a un administrador que configure el correo de salida.'}
              </Alert>
            )}
            {preview.smtpConfigurado && preview.remitenteEmail && (
              <Alert severity="info" variant="outlined">
                Se enviará desde: <strong>{preview.remitenteEmail}</strong>
                {preview.remitenteNombre ? ` (${preview.remitenteNombre})` : ''}
              </Alert>
            )}
            {preview.emailEnviadoAnteriormente && (
              <Alert severity="info" variant="outlined">
                Envío anterior registrado a: {preview.emailEnviadoAnteriormente}
              </Alert>
            )}
            {preview.emailsSugeridos.length === 0 && (
              <Alert severity="warning" variant="outlined">
                El proveedor no tiene un email válido en el sistema. Cargue un correo con formato
                {' '}
                <em>usuario@dominio.com</em>
                {' '}
                en el campo Para.
              </Alert>
            )}
            {emailError && (
              <Alert severity="error" variant="outlined" onClose={() => setEmailError(null)}>
                {emailError}
              </Alert>
            )}
            {preview.comprobanteLabel && (
              <Typography variant="body2" color="text.secondary">
                Comprobante: <strong>{preview.comprobanteLabel}</strong>
                {preview.nombreProveedor ? ` — ${preview.nombreProveedor}` : ''}
              </Typography>
            )}
            <TextField
              label="Para"
              value={destinatarios}
              onChange={(e) => {
                setDestinatarios(e.target.value);
                setEmailError(null);
              }}
              fullWidth
              required
              type="email"
              error={invalidDest.length > 0}
              helperText={
                invalidDest.length > 0
                  ? `Correo inválido: ${invalidDest.join(', ')}`
                  : 'Separar varios correos con ; o ,'
              }
              disabled={sending}
            />
            <TextField
              label="CC"
              value={cc}
              onChange={(e) => {
                setCc(e.target.value);
                setEmailError(null);
              }}
              fullWidth
              error={invalidCc.length > 0}
              helperText={
                invalidCc.length > 0 ? `Correo inválido: ${invalidCc.join(', ')}` : 'Opcional'
              }
              disabled={sending}
            />
            <TextField
              label="Asunto"
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              fullWidth
              required
              disabled={sending}
            />
            <TextField
              label="Mensaje"
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              disabled={sending}
            />
            <Typography variant="caption" color="text.secondary">
              Se adjuntará el PDF de la orden de pago al correo.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || sending || !puedeEnviar}
        >
          {sending ? 'Enviando…' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnviarOrdenPagoEmailDialog;
